import { eff } from "@/core/utils/effect-ts-abbr";
import { nextTick, watch, watchEffect } from "@vue/runtime-core";
import { comparer } from "mobx";
import * as tippy from "tippy.js";
import { DeepReadonly, ref } from "vue";
import { Emit, Model, Options, Prop, Vue } from "vue-property-decorator";
import { AppDropdownOptionProps } from "./app-dropdown-option";
import AppDropdownOptionComponent from "./app-dropdown-option.vue";
import { AppDropdownSearchProps } from "./app-dropdown-search";
import AppDropdownSearchComponent from "./app-dropdown-search.vue";
import { AppDropdownSelectedProps, defaultFormatValue } from "./app-dropdown-selected";
import AppDropdownSelectedComponent from "./app-dropdown-selected.vue";
import "./app-dropdown.scss";
import { Int } from "@effect-ts/system/Testing/Int";

export type AppDropdownOption = unknown;
export type AppDropdownOptionValue = unknown;
export type AppDropdownOptionLabel = string;
export type AppDropdownType = "single" | "single-optional" | "multiple";
export type AppDropdownOptionDetails = {
    readonly option: AppDropdownOption;
    readonly label: AppDropdownOptionLabel;
};
export type AppDropdownOptions = ReadonlyMap<
    AppDropdownOptionValue,
    AppDropdownOptionDetails
>;

const defaultGetLabel = (option: AppDropdownOption) =>
    ((option as string) || "").toString();

const defaultTrackBy: (
    option: AppDropdownOption,
    index: number,
) => symbol | string | number | boolean = (_option, index) => index;

/**
 * Low level dropdown component.
 *
 * Only essential functionality should be implemented here.
 *
 * Please keep is component as simple as possible.
 *
 * Probably it is better to create a new component based on this if you need some advanced features.
 */
@Options({
    components: {
        "app-dropdown-selected": AppDropdownSelectedComponent,
        "app-dropdown-option": AppDropdownOptionComponent,
        "app-dropdown-search": AppDropdownSearchComponent,
    },
})
export class AppDropdownComponent extends Vue {
    isOpen = false;

    @Prop()
    tabindex?: number;

    @Prop({ default: "" })
    placeholder!: string;

    @Prop()
    fontSize?: string;

    @Prop({ default: false, type: Boolean })
    showSelectedAsBox!: boolean;

    @Prop({ default: comparer.structural, type: Function })
    comparer!: (a: AppDropdownOptionValue, b: AppDropdownOptionValue) => boolean;

    /**
     * Type of the dropdown.
     *
     * If value is `single-optional`, then it is possible to choose single element or none.
     *
     * If value is `single`, then it is possible to choose single element,
     *   and it is not possible to unselect, also you must provide default value via `v-model`.
     *
     * If value is `multiple`, then it is possible to choose multiple elements or none.
     *
     */
    @Prop({ default: "single-optional" })
    type!: AppDropdownType;

    /**
     * Type should be different depending on `type` property:
     * | `type` property | type of the `value` property |
     * | single-optional | Option<T>                    |
     * | single          | T                            |
     * | multiple        | T[]                          |
     */
    @Model("modelValue")
    value: unknown;

    /**
     * Block dropdown when no options provided.
     */
    @Prop({ default: true, type: Boolean })
    blockEmpty!: boolean;

    /**
     * Block dropdown when single item provided in the options.
     */
    @Prop({ default: false, type: Boolean })
    blockSingle!: boolean;

    @Prop({ default: [] })
    options!: readonly AppDropdownOption[];

    @Prop({ default: {} })
    tippyProps!: DeepReadonly<Partial<tippy.Props>>;

    @Prop({ default: false })
    searchable!: boolean;

    @Prop({ default: "bottom" })
    placement!: tippy.Placement;

    @Prop({ default: false })
    arrow!: boolean;

    @Prop({ default: "light" })
    // see: https://vue-tippy.netlify.app/themes
    tippyTheme!: string;

    @Prop({ default: "shift-away" })
    // see: https://atomiks.github.io/tippyjs/v6/animations/
    animation!: string;

    @Prop({ default: true })
    // Set this option to false if you need customize dropdown styles.
    defaultTheme!: boolean;

    @Prop({ default: true })
    showCheckbox!: boolean;

    @Prop({ default: defaultTrackBy, type: Function })
    trackBy!: (
        option: AppDropdownOption,
        index: number,
    ) => symbol | string | number | boolean;

    @Prop({ default: defaultGetLabel, type: Function })
    getLabel!: (option: AppDropdownOption) => AppDropdownOptionLabel;

    @Prop({ default: eff.fn.identity, type: Function })
    getValue!: (option: AppDropdownOption) => AppDropdownOptionValue;

    @Prop({ default: defaultFormatValue, type: Function })
    formatValue!: AppDropdownSelectedProps["formatValue"];

    @Prop({ required: false })
    firstOption!: {
        readonly show?: boolean;
        readonly title: string;
        readonly url: string;
    };

    @Prop({ default: false, type: Boolean })
    disabled!: boolean;

    /**
     * We'll use a list even when the multiple mode is disabled.
     * It'll simplify implementation.
     *
     * If the multiple mode is disabled, then value can empty or contain single element,
     *   otherwise it can contain any number of elements.
     */
    internalValue: readonly AppDropdownOptionValue[] = [];

    searchResults: AppDropdownOptions = new Map();

    get optionDetails(): AppDropdownOptions {
        const values = this.blocked ? [] : this.internalValue;

        return eff.fn.pipe(
            // ensure reduce always receives an array
            this.options ?? [],
            eff.array.reduce(
                new Map<AppDropdownOptionValue, AppDropdownOptionDetails>(),
                (options, option): Map<unknown, AppDropdownOptionDetails> => {
                    const value = this.getValue(option);
                    const label = this.getLabel(option);

                    options.set(
                        eff.fn.pipe(
                            values,
                            eff.array.findFirst((_value) => this.comparer(_value, value)),
                            eff.option.fold(() => value, eff.fn.identity),
                        ),
                        {
                            option,
                            label,
                        },
                    );

                    return options;
                },
            ),
        );
    }

    get finalViewOptions(): readonly {
        readonly value: AppDropdownOptionValue;
        details: AppDropdownOptionDetails;
    }[] {
        const options = this.searchable ? this.searchResults : this.optionDetails;

        return eff.fn.pipe(
            [...options],
            eff.array.map(([value, details]) => ({ value, details })),
        );
    }

    get selectedValues(): ReadonlyMap<AppDropdownOptionValue, AppDropdownOptionDetails> {
        const options = this.optionDetails;
        const selectedLabels = new Map();

        const values = this.blocked ? this.optionDetails.keys() : this.internalValue;

        for (const value of values) {
            const option = options.get(value);
            if (option !== undefined) {
                selectedLabels.set(value, option);
            }
        }

        return selectedLabels;
    }

    removeOption(value: AppDropdownOptionValue) {
        this.internalValue = eff.fn.pipe(
            this.internalValue,
            eff.array.filter((it) => !this.comparer(it, value)),
        );
    }

    get slotSelectedProps(): AppDropdownSelectedProps {
        return {
            selectedValues: this.selectedValues,
            placeholder: this.placeholder,
            formatValue: this.formatValue,
            isAllSelected:
                !this.blocked &&
                this.selectedValues.size > 0 &&
                this.selectedValues.size === this.optionDetails.size,
            showSelectedAsBox: this.showSelectedAsBox,
            removeOption: this.removeOption,
        };
    }

    get slotSearchProps(): AppDropdownSearchProps {
        return {
            selectedValues: this.selectedValues,
            options: this.optionDetails,
            updateSearchResults: (searchResults) => {
                this.searchResults = searchResults;
            },
        };
    }

    get blocked() {
        return (
            (!this.options?.length && this.blockEmpty) ||
            (this.options?.length <= 1 && this.blockSingle)
        );
    }

    @Emit("blocked")
    emitBlocked() {
        return this.blocked;
    }

    @Emit("opened")
    opened() {
        return true;
    }

    @Emit("closed")
    closed() {
        return false;
    }

    mounted() {
        watchEffect(() => {
            this.emitBlocked();
        });

        watchEffect(() => {
            if (this.isOpen) {
                this.opened();
            } else {
                this.closed();
            }
        });

        watch(
            () => {
                return {
                    value: this.value,
                    type: this.type,
                };
            },
            ({ type, value }) => {
                switch (type) {
                    case "multiple":
                        this.internalValue = (value as unknown[]) || [];
                        break;
                    case "single-optional":
                        this.internalValue = eff.fn.pipe(
                            value as eff.option.Option<AppDropdownOptionValue>,
                            eff.option.fold(
                                () => [],
                                (value) => [value],
                            ),
                        );
                        break;
                    case "single":
                        this.internalValue = [value];
                        break;
                }
            },
            { immediate: true },
        );

        watch(
            () => {
                return {
                    internalValue: this.internalValue,
                    type: this.type,
                };
            },
            ({ type, internalValue }) => {
                let value: AppDropdownOptionValue;

                switch (type) {
                    case "multiple":
                        value = internalValue;
                        break;
                    case "single-optional":
                        value = eff.fn.pipe(internalValue, eff.array.head);
                        break;
                    case "single":
                        value = eff.fn.pipe(
                            internalValue,
                            eff.array.head,
                            eff.option.toUndefined,
                        );
                        break;
                }

                if (!this.comparer(this.value, value)) {
                    this.value = value;
                }
            },
        );
    }

    slotOptionProps(
        value: AppDropdownOptionValue,
        details: AppDropdownOptionDetails,
    ): AppDropdownOptionProps {
        return {
            selectedValues: this.selectedValues,
            details: details,
            value: value,
            showCheckbox: this.showCheckbox,
            toggle: () => this.toggleOption(value),
        };
    }

    toggleOption(value: AppDropdownOptionValue): void {
        if (this.selectedValues.has(value)) {
            if (this.type !== "single") {
                this.internalValue = eff.fn.pipe(
                    this.internalValue,
                    eff.array.filter((it) => !this.comparer(it, value)),
                );
            }
        } else if (this.type === "multiple") {
            this.internalValue = [...this.internalValue, value];
        } else {
            this.internalValue = [value];
            this.isOpen = false;
        }
    }

    getNextOrPrev(direction: "next" | "prev") {
        if (this.value === undefined || this.value === null) {
            this.internalValue = [1];
        } else if (direction === "next" && this.value === this.optionDetails.size) {
            this.internalValue = [1];
        } else if (direction === "prev" && this.value === 1) {
            this.internalValue = [this.optionDetails.size];
        } else if (direction === "next") {
            this.internalValue = [1 + (this.value as Int)];
        } else {
            this.internalValue = [(this.value as Int) - 1];
        }
    }
}

export default AppDropdownComponent;
